import { fetcher } from '#lib/backendFetcher';

/**
 * Shares a prospect query with a specific user.
 * @param {string} queryId - UUID of the prospect query
 * @param {Object} params
 * @param {string} params.sharedWithUserId - UUID of the target user
 * @param {string} [params.permission='read'] - Permission level ('read' or 'write')
 * @returns {Promise<Object>}
 */
export const shareProspectQuery = async (queryId, { sharedWithUserId, permission = 'read' }) => {
	return await fetcher('/api/v1/leads/shares', {
		method: 'POST',
		body: {
			resourceId: queryId,
			sharedWithUserId,
			permission,
		},
	});
};

/**
 * Shares a prospect query with the entire organization.
 * @param {string} queryId - UUID of the prospect query
 * @param {string} [permission='read'] - Permission level ('read' or 'write')
 * @returns {Promise<Object>}
 */
export const shareProspectQueryWithOrg = async (queryId, permission = 'read') => {
	return await fetcher('/api/v1/leads/shares', {
		method: 'POST',
		body: {
			resourceId: queryId,
			sharedWithUserId: null,
			permission,
		},
	});
};

/**
 * Revokes the share of a prospect query.
 * @param {string} shareId - UUID of the share
 * @returns {Promise<void>}
 */
export const unshareProspectQuery = async (shareId) => {
	await fetcher(`/api/v1/leads/shares/${shareId}`, {
		method: 'DELETE',
	});
};

/**
 * Fetches prospect queries shared with the current user (legacy).
 * @returns {Promise<Array<Object>>}
 */
export const fetchSharedQueriesWithMe = async () => {
	// Kept for backward compatibility: reads the sharedQueries field of the main queries response.
	const result = await fetcher('/api/v1/leads/queries');
	return result.sharedQueries ?? [];
};

/**
 * Removes the current user's share for a shared prospect query.
 * @param {string} resourceId - UUID of the prospect query
 * @returns {Promise<void>}
 */
export const removeShareForCurrentUser = async (resourceId) => {
	// Find the share for this resource and delete it
	const result = await fetcher(`/api/v1/leads/shares/query/${resourceId}`);
	const shares = result.shares ?? [];

	// Find the share that applies to the current user (the backend will handle auth context)
	const myShare = shares.find((s) => s.sharedWithUserId !== null) || shares[0];
	if (myShare) {
		await fetcher(`/api/v1/leads/shares/${myShare.id}`, {
			method: 'DELETE',
		});
	}
};

/**
 * Fetches all shares for a specific prospect query.
 * @param {string} queryId - UUID of the prospect query
 * @returns {Promise<Array<Object>>}
 */
export const fetchSharesForQuery = async (queryId) => {
	const result = await fetcher(`/api/v1/leads/shares/query/${queryId}`);
	return result.shares ?? [];
};
